import React from "react";
import { Box, Typography } from "@mui/material";
import { getPodcastImageUrl, getProgramImageUrl } from "../../utils/imageUtils";
import { formatMetric } from "./rankingCardMetrics";
import { ProgramThumbWithPodcastBadge, getPodcastBadgeLayout } from "./ProgramThumbWithPodcastBadge";
import { rankingThumbRadius } from "./rankingAssetDimensions";
import {
  RANKING_CAPTURE_FULL_TEXT_ATTR,
  rankingTableCaptureRowShellSx,
} from "./rankingTableLayout";

/**
 * Fixed layout: 780px total =
 *   header + gap(header→rows) + data block,
 *   data block = 10 row heights + 9 gaps between rows.
 */
const TABLE_HEIGHT_PX = 780;
const TABLE_HEADER_HEIGHT_PX = 20;
const TABLE_HEADER_TO_ROWS_GAP_PX = 10;
const TABLE_DATA_AREA_HEIGHT_PX =
  TABLE_HEIGHT_PX - TABLE_HEADER_HEIGHT_PX - TABLE_HEADER_TO_ROWS_GAP_PX;
const TABLE_ROW_GAP_PX = 10;
const TABLE_DATA_ROW_HEIGHT_PX =
  (TABLE_DATA_AREA_HEIGHT_PX - 9 * TABLE_ROW_GAP_PX) / 10;

const RANK_TITLE_FONT_PX = 24;
const METRIC_FONT_PX = 24;
const HEADER_LABEL_FONT_PX = 16;
const RANK_COL_WIDTH_PX = 28;
/** Fixed widths per column so headers and cells align; each flex row sizes independently otherwise. */
const PROMEDIO_COL_WIDTH_PX = 100;
const PEAK_COL_WIDTH_PX = 66;
const STREAMS_COL_WIDTH_PX = 60;
const THUMB_PX = 50;
const THUMB_RADIUS = `${rankingThumbRadius(THUMB_PX)}px`;
const ROW_INNER_GAP_PX = "8px";

/** Subset of channel row fields needed for the livestream table (avoids circular import). */
export interface LivestreamRankingChannel {
  _id: string;
  name: string;
  imageS3Key?: string;
  imageUrl?: string;
  imageUpdatedAt?: string | Date;
  updatedAt?: string | Date;
  /** Channel image for program-group rows (badge overlay on program cover). */
  channelImageS3Key?: string;
  avgLiveViewers?: number;
  peakLiveViewers?: number;
  /** Livestreams in the ranking period (API `episodeCount`). */
  livestreamCount?: number;
}

const livestreamRowCardBaseSx = {
  display: "flex",
  alignItems: "center",
  gap: "12px",
  px: "20px",
  py: 0,
  borderRadius: "20px",
  bgcolor: "rgba(33,35,45,0.7)",
  boxSizing: "border-box" as const,
  height: TABLE_DATA_ROW_HEIGHT_PX,
  minHeight: TABLE_DATA_ROW_HEIGHT_PX,
  maxHeight: TABLE_DATA_ROW_HEIGHT_PX,
  flexShrink: 0,
} as const;

/** Same horizontal box model as `livestreamRowCardBaseSx` so header columns line up with row content. */
const headerRowSx = {
  display: "flex",
  alignItems: "center",
  gap: "12px",
  px: "20px",
  py: 0,
  height: TABLE_HEADER_HEIGHT_PX,
  minHeight: TABLE_HEADER_HEIGHT_PX,
  maxHeight: TABLE_HEADER_HEIGHT_PX,
  borderRadius: "20px",
  boxSizing: "border-box" as const,
  flexShrink: 0,
} as const;

const headerLabelSx = {
  fontFamily: '"DM Sans", sans-serif',
  fontWeight: 600,
  color: "rgba(255,255,255,0.88)",
} as const;

function channelThumbUrl(channel: LivestreamRankingChannel): string {
  if (!channel.imageS3Key) return channel.imageUrl ?? "";
  return getProgramImageUrl({
    imageS3Key: channel.imageS3Key,
    imageUpdatedAt: channel.imageUpdatedAt,
  });
}

export interface LivestreamRankingTableProps {
  channels: LivestreamRankingChannel[];
  /** First data column header (Explorer: canal vs programa live). */
  nameColumnHeader?: string;
  /** PNG capture: wrap full titles instead of single-line ellipsis. */
  forCapture?: boolean;
}

/**
 * Top-10 livestream channels in card-style rows.
 * Columns: #, Canal + thumb, Promedio, Pico, Streams.
 */
export function LivestreamRankingTable({
  channels,
  nameColumnHeader = "Canal",
  forCapture = false,
}: LivestreamRankingTableProps) {
  const rowGap = `${TABLE_HEADER_TO_ROWS_GAP_PX}px`;
  const rows = channels.slice(0, 10);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: rowGap,
        width: "100%",
        height: forCapture ? "auto" : TABLE_HEIGHT_PX,
        minHeight: TABLE_HEIGHT_PX,
        maxHeight: forCapture ? "none" : TABLE_HEIGHT_PX,
        flexShrink: 0,
      }}
      aria-label="Ranking livestream"
    >
      <Box sx={headerRowSx}>
        <Box
          sx={{
            width: RANK_COL_WIDTH_PX,
            flexShrink: 0,
            textAlign: "center",
            fontSize: HEADER_LABEL_FONT_PX,
            lineHeight: `${TABLE_HEADER_HEIGHT_PX}px`,
            ...headerLabelSx,
          }}
        >
          #
        </Box>
        <Box
          sx={{
            flex: "1 1 0",
            minWidth: 0,
            fontSize: HEADER_LABEL_FONT_PX,
            lineHeight: `${TABLE_HEADER_HEIGHT_PX}px`,
            ...headerLabelSx,
          }}
        >
          {nameColumnHeader}
        </Box>
        <Box
          sx={{
            flex: `0 0 ${PROMEDIO_COL_WIDTH_PX}px`,
            width: PROMEDIO_COL_WIDTH_PX,
            textAlign: "right",
            fontSize: HEADER_LABEL_FONT_PX,
            lineHeight: `${TABLE_HEADER_HEIGHT_PX}px`,
            whiteSpace: "nowrap",
            ...headerLabelSx,
          }}
        >
          Promedio
        </Box>
        <Box
          sx={{
            flex: `0 0 ${PEAK_COL_WIDTH_PX}px`,
            width: PEAK_COL_WIDTH_PX,
            textAlign: "right",
            fontSize: HEADER_LABEL_FONT_PX,
            lineHeight: `${TABLE_HEADER_HEIGHT_PX}px`,
            whiteSpace: "nowrap",
            ...headerLabelSx,
          }}
        >
          Peak
        </Box>
        <Box
          sx={{
            flex: `0 0 ${STREAMS_COL_WIDTH_PX}px`,
            width: STREAMS_COL_WIDTH_PX,
            textAlign: "right",
            fontSize: HEADER_LABEL_FONT_PX,
            lineHeight: `${TABLE_HEADER_HEIGHT_PX}px`,
            whiteSpace: "nowrap",
            ...headerLabelSx,
          }}
        >
          Streams
        </Box>
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: `${TABLE_ROW_GAP_PX}px`,
          height: TABLE_DATA_AREA_HEIGHT_PX,
          minHeight: TABLE_DATA_AREA_HEIGHT_PX,
          maxHeight: TABLE_DATA_AREA_HEIGHT_PX,
          flexShrink: 0,
          boxSizing: "border-box",
        }}
      >
        {rows.map((channel, idx) => {
          const rank = idx + 1;
          const imgUrl = channelThumbUrl(channel);
          const channelBadgeUrl = channel.channelImageS3Key
            ? getPodcastImageUrl({ imageS3Key: channel.channelImageS3Key })
            : "";
          const showPodcastBadge = Boolean(
            channel.imageS3Key &&
              channel.channelImageS3Key &&
              imgUrl &&
              channelBadgeUrl,
          );
          const badgeLayout = showPodcastBadge
            ? getPodcastBadgeLayout(THUMB_PX)
            : null;
          const avg = channel.avgLiveViewers ?? 0;
          const peak = channel.peakLiveViewers ?? 0;
          const streams = channel.livestreamCount ?? 0;

          return (
            <Box
              key={`${channel._id}-${rank}`}
              sx={
                forCapture
                  ? { ...livestreamRowCardBaseSx, ...rankingTableCaptureRowShellSx }
                  : livestreamRowCardBaseSx
              }
            >
              <Typography
                sx={{
                  width: RANK_COL_WIDTH_PX,
                  flexShrink: 0,
                  textAlign: "center",
                  fontFamily: '"DM Serif Text", serif',
                  fontSize: RANK_TITLE_FONT_PX,
                  lineHeight: 1.15,
                  color: "#fff",
                }}
              >
                {rank}.
              </Typography>
              <Box
                sx={{
                  flex: "1 1 0",
                  minWidth: 0,
                  display: "flex",
                  alignItems: "center",
                  gap: ROW_INNER_GAP_PX,
                }}
              >
                <Box
                  sx={{
                    width: badgeLayout ? badgeLayout.view.w : THUMB_PX,
                    height: badgeLayout ? badgeLayout.view.h : THUMB_PX,
                    flexShrink: 0,
                    borderRadius: badgeLayout ? 0 : THUMB_RADIUS,
                    overflow: "hidden",
                    bgcolor: showPodcastBadge
                      ? "transparent"
                      : "rgba(33,35,45,0.7)",
                    position: "relative",
                  }}
                >
                  {imgUrl && showPodcastBadge ? (
                    <ProgramThumbWithPodcastBadge
                      coverUrl={imgUrl}
                      podcastImageUrl={channelBadgeUrl}
                      maskId={`livestream-podcast-badge-cutout-${channel._id}`}
                      sizePx={THUMB_PX}
                      contained
                    />
                  ) : imgUrl ? (
                    <Box
                      component="img"
                      src={imgUrl}
                      alt=""
                      sx={{
                        width: "102%",
                        height: "102%",
                        objectFit: "cover",
                        margin: "-1%",
                        display: "block",
                      }}
                    />
                  ) : (
                    <Box
                      sx={{
                        width: "100%",
                        height: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontFamily: '"DM Serif Text", serif',
                        fontSize: RANK_TITLE_FONT_PX,
                        color: "#fff",
                      }}
                    >
                      {channel.name.charAt(0).toUpperCase()}
                    </Box>
                  )}
                </Box>
                <Typography
                  {...{ [RANKING_CAPTURE_FULL_TEXT_ATTR]: forCapture ? "" : undefined }}
                  sx={{
                    fontFamily: '"DM Serif Text", serif',
                    fontSize: RANK_TITLE_FONT_PX,
                    lineHeight: 1.2,
                    color: "#fff",
                    minWidth: 0,
                    ...(forCapture
                      ? {
                          whiteSpace: "normal",
                          overflow: "visible",
                          wordBreak: "break-word",
                        }
                      : {
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          display: "block",
                          whiteSpace: "nowrap",
                        }),
                  }}
                >
                  {channel.name}
                </Typography>
              </Box>
              <Typography
                sx={{
                  flex: `0 0 ${PROMEDIO_COL_WIDTH_PX}px`,
                  width: PROMEDIO_COL_WIDTH_PX,
                  textAlign: "right",
                  fontFamily: '"DM Serif Text", serif',
                  fontSize: METRIC_FONT_PX,
                  lineHeight: `${METRIC_FONT_PX}px`,
                  color: "#fff",
                  whiteSpace: "nowrap",
                }}
              >
                {formatMetric(avg, "livestream-avg")}
              </Typography>
              <Typography
                sx={{
                  flex: `0 0 ${PEAK_COL_WIDTH_PX}px`,
                  width: PEAK_COL_WIDTH_PX,
                  textAlign: "right",
                  fontFamily: '"DM Serif Text", serif',
                  fontSize: METRIC_FONT_PX,
                  lineHeight: `${METRIC_FONT_PX}px`,
                  color: "#fff",
                  whiteSpace: "nowrap",
                }}
              >
                {formatMetric(peak, "livestream-avg")}
              </Typography>
              <Typography
                sx={{
                  flex: `0 0 ${STREAMS_COL_WIDTH_PX}px`,
                  width: STREAMS_COL_WIDTH_PX,
                  textAlign: "right",
                  fontFamily: '"DM Serif Text", serif',
                  fontSize: METRIC_FONT_PX,
                  lineHeight: `${METRIC_FONT_PX}px`,
                  color: "#fff",
                  whiteSpace: "nowrap",
                }}
              >
                {streams}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}
